import { motion } from 'framer-motion';
import { Check, X, ShieldCheck, CalendarClock, HeartPulse } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const HAIR_IMG =
  'https://images.unsplash.com/photo-1618049049816-43a00d5b0c3d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200';

const ROWS = [
  { label: 'Rasur nötig', classic: 'Ja, meist komplett', lhf: 'Nein, keinerlei Rasur', good: true },
  { label: 'Sichtbarkeit danach', classic: 'Wochenlang sichtbar', lhf: 'Sofort kaschiert', good: true },
  { label: 'Gesellschaftsfähig', classic: 'Nach 2–4 Wochen', lhf: 'Am nächsten Tag', good: true },
  { label: 'Ergebnis-Vorschau', classic: 'Erst nach Monaten', lhf: 'Direkt in voller Länge', good: true },
  { label: 'Qualität der Grafts', classic: 'FUE Saphir Standard', lhf: 'FUE Saphir Standard', good: true },
];

export const ComparisonBento = () => {
  const { isDark } = useTheme();
  return (
    <section
      data-testid="lhf-comparison"
      className={`py-24 md:py-32 px-6 ${isDark ? 'bg-slate-900' : 'bg-white'}`}
    >
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className={`text-4xl sm:text-5xl font-extralight text-center mb-16 ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Klassische FUE <span className="text-[#7BA7C2]">vs.</span> Long Hair FUE
        </motion.h2>

        <div className="grid lg:grid-cols-5 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-2 rounded-2xl overflow-hidden relative min-h-[320px]"
          >
            <img
              src={HAIR_IMG}
              alt="Volles gesundes Haar nach Long Hair FUE Haartransplantation ohne Rasur"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
            <div className="absolute bottom-0 p-8">
              <p className="text-white text-2xl font-extralight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Gleiche Präzision.
                <br />
                <span className="text-[#9BBFD9]">Null Sichtbarkeit.</span>
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className={`lg:col-span-3 rounded-2xl border overflow-hidden ${
              isDark ? 'border-slate-800 bg-slate-950/60' : 'border-slate-200 bg-slate-50/80'
            }`}
          >
            <div className={`grid grid-cols-3 text-sm font-medium px-6 py-4 border-b ${
              isDark ? 'border-slate-800 text-slate-400' : 'border-slate-200 text-slate-500'
            }`}>
              <span />
              <span className="text-center">Klassische FUE</span>
              <span className="text-center text-[#7BA7C2]">Long Hair FUE</span>
            </div>
            {ROWS.map((r, i) => (
              <motion.div
                key={r.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className={`grid grid-cols-3 items-center px-6 py-4 text-sm ${
                  i !== ROWS.length - 1 ? (isDark ? 'border-b border-slate-800/60' : 'border-b border-slate-200/70') : ''
                }`}
              >
                <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{r.label}</span>
                <span className={`text-center flex items-center justify-center gap-1.5 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                  {i < 4 && <X className="w-3.5 h-3.5 text-rose-400 shrink-0" />}
                  {r.classic}
                </span>
                <span className={`text-center flex items-center justify-center gap-1.5 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  {r.lhf}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const PricingBlock = () => {
  const { isDark } = useTheme();
  return (
    <section
      data-testid="lhf-pricing"
      className={`py-24 md:py-32 px-6 ${isDark ? 'bg-slate-950' : 'bg-slate-50'}`}
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className={`rounded-2xl border overflow-hidden ${
            isDark ? 'border-[#7BA7C2]/30 bg-slate-900' : 'border-[#7BA7C2]/40 bg-white'
          } shadow-2xl shadow-[#7BA7C2]/10`}
        >
          <div className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] px-8 py-3 text-white text-sm font-medium text-center">
            Transparente Festpreise — keine versteckten Kosten
          </div>
          <div className="p-8 md:p-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <h3
                  className={`text-3xl font-extralight ${isDark ? 'text-white' : 'text-slate-900'}`}
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Long Hair FUE
                </h3>
                <p className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  FUE Saphir Basis + Long-Hair-Aufpreis
                </p>
              </div>
              <div className="text-right">
                <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>ab</span>
                <span className="ml-2 text-5xl font-light bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
                  5.999€
                </span>
              </div>
            </div>

            <div className={`mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              {[
                'FUE Saphir Basis (bis 2.000 Grafts) ab 2.999€',
                'Long Hair FUE Aufpreis +3.000€',
                'Komplett ohne Rasur',
                '12 Monate Nachbetreuung inklusive',
                '2 regenerative Haartherapien inklusive',
                'Betäubung & Nachsorgekit inklusive',
              ].map((f) => (
                <div key={f} className="flex items-start gap-2.5">
                  <Check className="w-4 h-4 text-[#7BA7C2] mt-0.5 shrink-0" />
                  {f}
                </div>
              ))}
            </div>

            <div
              className={`mt-10 flex flex-col sm:flex-row items-center gap-6 rounded-2xl p-6 ${
                isDark ? 'bg-slate-950/70' : 'bg-slate-50'
              }`}
            >
              <div className="flex gap-5">
                <CalendarClock className="w-8 h-8 text-[#7BA7C2] shrink-0" />
                <div>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Ratenzahlung ab 53€/Monat
                  </p>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    6 Monate zinsfrei · Laufzeiten bis 72 Monate · über Medipay
                  </p>
                </div>
              </div>
              <a
                href="/preisrechner"
                data-testid="lhf-pricing-calculator-btn"
                className="sm:ml-auto shrink-0 px-6 py-3 rounded-full bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] text-white text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Kosten berechnen
              </a>
            </div>

            <div className={`mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#7BA7C2]" /> Behandlung in Deutschland</span>
              <span className="flex items-center gap-1.5"><HeartPulse className="w-3.5 h-3.5 text-[#7BA7C2]" /> Ärztlich geleitet</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#7BA7C2]" /> 4.9★ Google Bewertung</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
